'use client';
import React from 'react';
import { Form, Input, Checkbox } from 'antd';
import { useTranslations } from 'next-intl';
import InputForm from '@/ui/shared/components/inputForm';
import { useController } from './Controller';
import ErrorAlert from '@/ui/features/authentication/components/errorAlert';
import Image from 'next/image';
import { Link } from '@/intl/navigation';
import appRoutes from '@/constants/routes';
import AfiwaButton from '@/ui/shared/components/afiwaButton';
import { useSearchParams } from 'next/navigation';

function SignupForm() {
  const {
    passwordVisible,
    confirmPasswordVisible,
    setPasswordVisible,
    setConfirmPasswordVisible,
    loading,
    apiError,
    handleSignup,
  } = useController();

  const signupPageParams = useSearchParams();
  const affiliateParnter = signupPageParams.get('ref');

  const [form] = Form.useForm();
  const auth = useTranslations('auth');
  const textNav = useTranslations('navigation');

  const handleSubmit = async (values: any) => {
    const payload = {
      username: values.username,
      email: values.email,
      password: values.password,
      ...(affiliateParnter && { refferedBy: affiliateParnter }),
    };

    await handleSignup(payload);
  };

  return (
    <Form
      layout="vertical"
      onFinish={handleSubmit}
      form={form}
      name="register"
      scrollToFirstError
      requiredMark={false}
    >
      <ErrorAlert errorType={apiError} />

      {/* NAME */}
      <InputForm
        label={auth('forms.labels.username')}
        name="username"
        placeholder={auth('forms.placeholders.username')}
        rules={[
          {
            required: true,
            message: auth('forms.errors.fillUsername'),
            whitespace: true,
          },
          { min: 3, message: auth('forms.errors.shortName') },
        ]}
        component={<Input size="large" />}
      />

      {/* EMAIL */}
      <InputForm
        label={auth('forms.labels.email')}
        name="email"
        placeholder={auth('forms.placeholders.sampleMail')}
        rules={[
          { required: true, message: auth('forms.errors.fillEmail') },
          { type: 'email', message: auth('forms.errors.invalidMail') },
        ]}
        component={
          <Input
            size="large"
            prefix={
              <Image
                src={'/icons/enveloppe.svg'}
                alt="enveloppe-icon"
                height={16}
                width={16}
                className={'mr-2'}
              />
            }
            onInput={(e) =>
              (e.currentTarget.value =
                e.currentTarget.value.toLocaleLowerCase())
            }
          />
        }
      />

      {/* PASSWORD */}
      <InputForm
        label={auth('forms.labels.password')}
        name="password"
        placeholder={auth('forms.placeholders.password')}
        rules={[
          { required: true, message: auth('forms.errors.fillPassword') },
          { min: 5, message: auth('forms.errors.shortPass') },
        ]}
        component={
          <Input.Password
            size="large"
            type={passwordVisible ? 'text' : 'password'}
            visibilityToggle={{
              visible: passwordVisible,
              onVisibleChange: setPasswordVisible,
            }}
            prefix={
              <Image
                src={'/icons/lock.svg'}
                alt="lock-icon"
                height={16}
                width={16}
                className={'mr-2'}
              />
            }
          />
        }
      />

      {/* REPEAT-PASSWORD */}
      <InputForm
        label={auth('forms.labels.confirmPassword')}
        name="confirmPassword"
        placeholder={auth('forms.placeholders.confirmPassword')}
        dependencies={['password']}
        rules={[
          { required: true, message: auth('forms.errors.fillPassword') },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue('password') === value) {
                return Promise.resolve();
              }
              return Promise.reject(
                new Error(auth('forms.errors.mismatchPassword')),
              );
            },
          }),
        ]}
        component={
          <Input.Password
            size="large"
            visibilityToggle={{
              visible: confirmPasswordVisible,
              onVisibleChange: setConfirmPasswordVisible,
            }}
            prefix={
              <Image
                src={'/icons/lock.svg'}
                alt="lock-icon"
                height={16}
                width={16}
                className={'mr-2'}
              />
            }
          />
        }
      />

      {/* AGREEMENT */}
      <InputForm
        name="agreement"
        valuePropName="checked"
        rules={[
          {
            validator: (_, value) =>
              value
                ? Promise.resolve()
                : Promise.reject(new Error(auth('forms.errors.terms'))),
          },
        ]}
        component={
          <div className="flex flex-row items-center space-x-2">
            <Checkbox />
            <p className={`ml-auto flex  flex-row sm:text-xs lg:text-xs`}>
              <span className="mr-2  font-bold text-gray-900">
                {auth('forms.labels.agreeTerms')}
              </span>

              <Link
                href={appRoutes.terms}
                className={`font-semibold text-orange-200 underline
                   hover:text-orange-200 hover:underline`}
              >
                {textNav('terms')}
              </Link>
            </p>
          </div>
        }
      />

      <AfiwaButton
        htmlType="submit"
        type="primary"
        loading={loading}
        text={auth('createAccount')}
        className="mt-6 flex w-full justify-center"
      />
    </Form>
  );
}

export default SignupForm;
